import React, { useEffect, useMemo } from "react";
import { Progress, Card, Tag, Skeleton, Button } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, GiftOutlined, } from "@ant-design/icons";
import SideBar from "../component/SideBar";
import { useOrders } from "../context/ordersContext";
import { useSettings } from "../context/settingContext";
import { Link } from "react-router-dom";

const Dashboard = () => {
    const { getOrders, loading, ordersData, wishItem, getWishItem } = useOrders();
    const { user } = useSettings();
    const userId = user?.id;

    useEffect(() => {
        if (userId) {
            getWishItem(userId);
            getOrders(userId);
        }
    }, [userId]);

    console.log(ordersData)

    const { completedCount, pendingCount, wishlistCount, totalCount } = useMemo(() => {
        const completed = ordersData.filter(
            (o) => o.status === "Delivered" || o.status === "confirmed" || o.status === "created"
        ).length;

        const pending = ordersData.filter(
            (o) => o.status === "pending" || o.status === "Pending"
        ).length;

        const wishlist = Array.isArray(wishItem) ? wishItem.length : 0;
        const total = ordersData.length;

        return {
            completedCount: completed,
            pendingCount: pending,
            wishlistCount: wishlist,
            totalCount: total,
        };
    }, [ordersData, wishItem]);

    const scaleProgress = (count) => Math.min((count / 10) * 100, 100);

    const recentOrders = [...ordersData]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);

    const renderSkeletonTable = () => (
        <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
                <div
                    key={i}
                    className="grid grid-cols-6 gap-4 items-center bg-white p-3 rounded-lg shadow-sm"
                >
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                    <Skeleton.Input active size="small" style={{ width: 140 }} />
                    <Skeleton.Input active size="small" style={{ width: 120 }} />
                    <Skeleton.Input active size="small" style={{ width: 90 }} />
                    <Skeleton.Input active size="small" style={{ width: 80 }} />
                    <Skeleton.Button active size="small" style={{ width: 60 }} />
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex min-h-screen bg-gray-100 pt-[160px] pb-[50px]">
            <div className="w-64 bg-brandBlue shadow-lg py-8 px-4 text-white rounded-tr-[10px] rounded-br-[10px]">
                <h2 className="text-2xl font-bold mb-10">My Account</h2>
                <SideBar />
            </div>

            <div className="flex-1 p-10 pt-0 pb-0">
                <h1 className="text-3xl font-bold mb-8 text-gray-800">
                    Welcome Back 👋
                </h1>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <Card className="shadow-lg hover:shadow-xl transition rounded-xl">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-semibold text-gray-600 mb-2">
                                    Orders Completed
                                </h3>
                                <Progress
                                    type="circle"
                                    strokeColor="#0bf50b"
                                    percent={scaleProgress(completedCount)}
                                    format={() => `${completedCount}`}
                                />
                            </div>
                            <CheckCircleOutlined className="text-3xl text-green-500" />
                        </div>
                    </Card>
                    <Card className="shadow-lg hover:shadow-xl transition rounded-xl">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-semibold text-gray-600 mb-2">
                                    Orders Pending
                                </h3>
                                <Progress
                                    type="circle"
                                    status="active"
                                    strokeColor="#f59e0b"
                                    percent={scaleProgress(pendingCount)}
                                    format={() => `${pendingCount}`}
                                />
                            </div>
                            <ClockCircleOutlined className="text-3xl text-yellow-500" />
                        </div>
                    </Card>
                    <Card className="shadow-lg hover:shadow-xl transition rounded-xl">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-semibold text-gray-600 mb-2">
                                    Wishlist Items
                                </h3>
                                <Progress
                                    type="circle"
                                    percent={scaleProgress(wishlistCount)}
                                    strokeColor="#0bf5f5"
                                    format={() => `${wishlistCount}`}
                                />
                            </div>
                            <GiftOutlined className="text-3xl text-pink-500" />
                        </div>
                    </Card>
                </div>

                <Card className="shadow-lg rounded-xl">
                    <h3 className="font-semibold text-lg mb-5 text-gray-700">
                        Recent Orders
                    </h3>

                    {loading ? (
                        renderSkeletonTable()
                    ) : recentOrders.length === 0 ? (
                        <div className="not_found_wrp">No recent orders found.</div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-200 text-gray-700">
                                    <th className="p-3">Order ID</th>
                                    <th className="p-3">Date</th>
                                    <th className="p-3">Status</th>
                                    <th className="p-3">Amount</th>
                                    <th className="p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentOrders.map((order) => (
                                    <tr key={order.id} className="border-b hover:bg-gray-50">
                                        <td className="p-3 font-medium">{order.orderId}</td>
                                        <td className="p-3">
                                            {new Date(order.date).toLocaleDateString()}
                                        </td>
                                        <td className="p-3">
                                            <Tag
                                                color={
                                                    order.status === "Delivered" || order.status === "confirmed" || order.status === "created"
                                                        ? "green"
                                                        : order.status === "pending" || order.status === "Pending"
                                                            ? "gold"
                                                            : "red"
                                                }
                                            >
                                                {order.status?.toUpperCase()}
                                            </Tag>
                                        </td>
                                        <td className="p-3 font-semibold text-brandBlue">
                                            {order.total}
                                        </td>
                                        <td>
                                            <Link to={`/dashboard/order/${order.key}`}>
                                                <Button
                                                    icon={<EyeOutlined />}
                                                    type="default"
                                                    className="!border !border-brandBlue !text-brandBlue font-semibold hover:!bg-brandBlue hover:!text-white transition-all duration-300 !rounded-lg"
                                                    style={{
                                                        background: "rgba(0, 119, 255, 0.05)",
                                                        backdropFilter: "blur(4px)",
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;
